/**
 * Graph Builder Edit Modal
 *
 * レポート内のGraphBuilder要素を編集するためのモーダル
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Dialog } from '../common/Dialog';
import { Button } from '../common/Button';
import type { GraphBuilderElement } from '../../../types/report.types';
import type { DataSet } from '../../../types/dataset.types';
import type { GraphConfig, GraphCallbacks, AspectRatioPreset } from '../../../types/graph-builder.types';
import { graphRegistry } from './GraphRegistry';
import { GraphSettings } from './GraphSettings';
import { GraphPreview } from './GraphPreview';
import { getAspectRatioValue, calculateSizeWithAspectRatio } from '../../utils/aspectRatioUtils';
import logger from '../../utils/logger';
import * as styles from '../../styles/components/graph-builder.css.ts';

interface GraphBuilderEditModalProps {
  isOpen: boolean;
  element: GraphBuilderElement;
  dataset: DataSet;
  onSave: (newConfig: GraphConfig, aspectRatio?: AspectRatioPreset) => void;
  onClose: () => void;
}

export function GraphBuilderEditModal({
  isOpen,
  element,
  dataset,
  onSave,
  onClose
}: GraphBuilderEditModalProps) {
  const { graphConfig } = element;

  // 編集中の設定（ディープコピーで独立したオブジェクトを作成）
  const [editConfig, setEditConfig] = useState<GraphConfig>(() => JSON.parse(JSON.stringify(graphConfig)));
  const [editAspectRatio, setEditAspectRatio] = useState<AspectRatioPreset>(element.aspectRatio || '16:9');

  // プレビュー領域のref
  const previewSectionRef = useRef<HTMLDivElement>(null);

  // アスペクト比に基づく計算済みサイズ
  const [calculatedSize, setCalculatedSize] = useState<{ width: number; height: number } | null>(null);

  // 親コンテナのサイズを監視してアスペクト比に基づくサイズを計算
  useEffect(() => {
    const ratio = getAspectRatioValue(editAspectRatio);
    if (ratio === null || !previewSectionRef.current) {
      setCalculatedSize(null);
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width: containerWidth, height: containerHeight } = entry.contentRect;
        // パディングを考慮（previewSectionのpadding: 12px）
        const size = calculateSizeWithAspectRatio(containerWidth, containerHeight, ratio, 12);
        setCalculatedSize(size);
      }
    });

    resizeObserver.observe(previewSectionRef.current);
    return () => resizeObserver.disconnect();
  }, [editAspectRatio]);

  // グラフタイプを取得
  const graphType = editConfig.type;

  // 現在のグラフタイプのみを表示（タイプ変更は不可）
  const graphTypes = useMemo(() => {
    const entry = graphRegistry.get(graphType);
    if (!entry) return [];
    return [{
      value: graphType,
      label: entry.name,
      description: entry.description
    }];
  }, [graphType]);

  // ダミーのコールバック（編集モーダルでは選択機能は不要）
  const dummyCallbacks: GraphCallbacks = useMemo(() => ({
    onElementSelect: () => {},
    selectedRows: []
  }), []);

  // モーダルが開いたときに状態をリセット（ディープコピーで独立したオブジェクトを作成）
  useEffect(() => {
    if (isOpen) {
      setEditConfig(JSON.parse(JSON.stringify(graphConfig)));
      setEditAspectRatio(element.aspectRatio || '16:9');
      logger.info('[GraphBuilderEditModal] Modal opened, config reset');
    }
  }, [isOpen, graphConfig, element.aspectRatio]);

  // グラフタイプ変更（現在は無効化 - 同じタイプのみ選択可能）
  const handleGraphTypeChange = useCallback(() => {
    // タイプ変更は許可しない
    logger.info('[GraphBuilderEditModal] Graph type change not supported in edit mode');
  }, []);

  // 設定変更
  const handleConfigChange = useCallback((newConfig: GraphConfig) => {
    setEditConfig(newConfig);
  }, []);

  // アスペクト比変更
  const handleAspectRatioChange = useCallback((ratio: AspectRatioPreset) => {
    setEditAspectRatio(ratio);
  }, []);

  // 保存
  const handleSave = useCallback(() => {
    onSave(editConfig, editAspectRatio);
    onClose();
    logger.info('[GraphBuilderEditModal] Config saved');
  }, [editConfig, editAspectRatio, onSave, onClose]);

  // バリデーションエラー
  const validationError = useMemo(() => {
    if (!graphType || !editConfig) return undefined;
    const entry = graphRegistry.get(graphType);
    if (!entry) return 'Unknown graph type';
    return entry.renderer.validate(editConfig, dataset) || undefined;
  }, [graphType, editConfig, dataset]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Graph"
      width="fullscreen"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!!validationError}
          >
            Save
          </Button>
        </>
      }
    >
      <div className={styles.twoColumnLayout} style={{ height: '100%', minHeight: 0 }}>
        {/* 設定パネル（左） */}
        <div className={styles.settingsPane}>
          <GraphSettings
            graphType={graphType}
            config={editConfig}
            dataset={dataset}
            graphTypes={graphTypes}
            aspectRatio={editAspectRatio}
            onGraphTypeChange={handleGraphTypeChange}
            onConfigChange={handleConfigChange}
            onAspectRatioChange={handleAspectRatioChange}
          />
        </div>

        {/* プレビュー領域（右） */}
        <div className={styles.previewPane}>
          <div ref={previewSectionRef} className={styles.previewSection}>
            <div
              className={styles.previewContainer}
              style={(() => {
                // calculatedSizeがある場合は明示的なサイズを使用
                if (calculatedSize) {
                  return {
                    width: `${calculatedSize.width}px`,
                    height: `${calculatedSize.height}px`,
                    alignSelf: 'center',
                    margin: 'auto'
                  };
                }
                // autoの場合はコンテナに合わせる（デフォルト）
                return undefined;
              })()}
            >
              <GraphPreview
                graphType={graphType}
                config={editConfig}
                dataset={dataset}
                callbacks={dummyCallbacks}
                validationError={validationError}
              />
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
